import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ReceiptText, ShoppingCart } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { PanelLateral } from '@/shared/ui/PanelLateral';
import { enDinero } from '@/shared/ui/graficos';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { DialogoDeRetencion } from './DialogoDeRetencion';
import type { Comprobante, Retencion } from './contrato';

const IMPORTES: ReadonlyArray<{ clave: string; rotulo: string }> = [
  { clave: 'subtotalSinImpuestos', rotulo: 'Subtotal sin impuestos' },
  { clave: 'baseGravada', rotulo: 'Base gravada' },
  { clave: 'baseCero', rotulo: 'Base tarifa 0 %' },
  { clave: 'baseNoObjeto', rotulo: 'Base no objeto de IVA' },
  { clave: 'baseExenta', rotulo: 'Base exenta' },
  { clave: 'descuento', rotulo: 'Descuento' },
  { clave: 'ice', rotulo: 'ICE' },
  { clave: 'propina', rotulo: 'Propina' },
  { clave: 'iva', rotulo: 'IVA' },
  { clave: 'total', rotulo: 'Total' },
];

function enFecha(iso: string | undefined): string {
  if (!iso) return '—';
  const [anio, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${anio}`;
}

export function DetalleDelComprobante({ id, alCerrar }: { id: number; alCerrar: () => void }) {
  const [reteniendo, setReteniendo] = useState(false);
  const { puede } = useSesion();

  const comprobante = useQuery({
    queryKey: ['compras', id],
    queryFn: () => api.get<Comprobante>(`/compras/${id}`),
  });

  // La que ya tiene, si la tiene: una compra se retiene una sola vez, y conviene verlo aquí
  // antes de intentar hacer otra.
  const retencion = useQuery({
    queryKey: ['retencion-de-compra', id],
    queryFn: () =>
      api
        .get<{ contenido: Retencion[] }>('/retenciones', { query: { buscar: '', tamanio: 200 } })
        .then((p) => p.contenido.find((r) => r.comprobanteId === id && r.estado !== 'anulada')),
    enabled: puede('compras.retenciones.ver'),
  });

  const c = comprobante.data;
  const yaRetenida = retencion.data;

  return (
    <PanelLateral
      abierto
      alCerrar={alCerrar}
      icono={ShoppingCart}
      titulo={c ? `${c.tipoEtiqueta} ${c.numero}` : 'Comprobante'}
      descripcion={c?.proveedorRazonSocial ?? 'Cargando…'}
    >
      {c && (
        <div className="space-y-5">
          {!c.cuadra && (
            <p className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-justify text-[11px] uppercase leading-relaxed text-amber-900">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
              <span>
                <span className="font-semibold">Este comprobante no cuadra consigo mismo.</span>{' '}
                Sus bases no suman el subtotal que declara. Se registró tal cual —lo que dice el
                proveedor es lo que se declara— pero conviene revisarlo antes de pagarlo.
              </span>
            </p>
          )}

          {!c.datosCompletos && (
            <p className="rounded-xl border border-armazon-200 bg-armazon-50 p-4 text-justify text-[11px] uppercase leading-relaxed text-armazon-600">
              <span className="font-semibold">Esto es lo que dice el listado del SRI.</span> Falta
              su XML, que trae las bases por tarifa, la forma de pago y el detalle. Sin eso no se
              puede declarar: cárguelo con «Importar XML» y este comprobante se completará solo.
            </p>
          )}

          {c.estado === 'anulada' && (
            <p className="rounded-xl border border-armazon-200 bg-armazon-50 p-4 text-justify text-[11px] uppercase leading-relaxed text-armazon-600">
              <span className="font-semibold">Anulado.</span> {c.motivoAnulacion}
            </p>
          )}

          {puede('compras.retenciones.emitir') && c.estado !== 'anulada' && (
            <div className="rounded-xl border border-armazon-200 bg-armazon-50/60 p-4">
              {yaRetenida ? (
                <p className="text-[11px] uppercase leading-relaxed tracking-wide text-armazon-600">
                  <span className="font-semibold">Ya tiene retención</span>
                  {yaRetenida.numero ? ` ${yaRetenida.numero}` : ' en borrador'} por $
                  {yaRetenida.totalRetenido.toFixed(2)}. Para hacer otra hay que anular esa
                  primero.
                </p>
              ) : (
                <>
                  <p className="text-[11px] uppercase leading-relaxed tracking-wide text-armazon-600">
                    De esta compra hay que retenerle al proveedor y entregarle el comprobante.
                  </p>
                  <button
                    type="button"
                    onClick={() => setReteniendo(true)}
                    className="tactil mt-3 inline-flex items-center gap-2 rounded-xl bg-marca-600 px-4 py-2 text-xs font-bold uppercase tracking-wide text-white transition hover:bg-marca-700"
                  >
                    <ReceiptText className="size-4" aria-hidden />
                    Retener de esta compra
                  </button>
                </>
              )}
            </div>
          )}

          <dl className="space-y-3 text-xs">
            <Dato rotulo="Proveedor" valor={c.proveedorRazonSocial} />
            <Dato rotulo="Identificación" valor={c.proveedorIdentificacion} />
            <Dato rotulo="Emisión" valor={enFecha(c.fechaEmision)} />
            <Dato rotulo="Autorización" valor={enFecha(c.fechaAutorizacion)} />
            <Dato rotulo="Sustento tributario" valor={`${c.sustento} · ${c.sustentoEtiqueta}`} />
            {c.formaPago && <Dato rotulo="Forma de pago" valor={c.formaPago} />}
            {c.claveAcceso && <Dato rotulo="Clave de acceso" valor={c.claveAcceso} />}
            {c.observaciones && <Dato rotulo="Observaciones" valor={c.observaciones} />}
            <Dato
              rotulo="Lo registró"
              valor={`${c.registradoPor ?? '—'} · desde ${c.origen.toUpperCase()}`}
            />
          </dl>

          <div className="rounded-xl border border-armazon-200 p-4">
            <p className="text-[10px] font-bold uppercase tracking-wide text-armazon-500">
              Importes
            </p>
            <dl className="mt-2 space-y-1.5">
              {IMPORTES.filter(
                (i) => (c.importes[i.clave] ?? 0) !== 0 || i.clave === 'total',
              ).map((i) => (
                <div key={i.clave} className="flex items-baseline justify-between gap-3">
                  <dt
                    className={`text-[11px] uppercase ${
                      i.clave === 'total' ? 'font-bold text-armazon-800' : 'text-armazon-500'
                    }`}
                  >
                    {i.rotulo}
                  </dt>
                  <dd
                    className={`importe tabular-nums ${
                      i.clave === 'total'
                        ? 'text-sm font-bold text-armazon-900'
                        : 'text-xs text-armazon-700'
                    }`}
                  >
                    {enDinero(c.importes[i.clave] ?? 0)}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          {c.lineas.length > 0 && (
            <div className="rounded-xl border border-armazon-200">
              <p className="border-b border-armazon-100 px-4 py-2.5 text-[10px] font-bold uppercase tracking-wide text-armazon-500">
                Qué se compró
              </p>
              <ul className="divide-y divide-armazon-100">
                {c.lineas.map((l) => (
                  <li key={l.orden} className="px-4 py-2.5">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="min-w-0 text-xs uppercase text-armazon-800">
                        {l.descripcion}
                      </span>
                      <span className="importe shrink-0 text-xs font-semibold tabular-nums text-armazon-900">
                        {enDinero(l.subtotal)}
                      </span>
                    </div>
                    <div className="mt-0.5 text-[10px] uppercase text-armazon-400">
                      {l.cantidad} × {enDinero(l.precioUnitario)}
                      {l.ivaTarifa > 0 && ` · IVA ${l.ivaTarifa} %`}
                      {l.codigoPrincipal && ` · ${l.codigoPrincipal}`}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {reteniendo && c && (
        <DialogoDeRetencion compra={c} alCerrar={() => setReteniendo(false)} />
      )}
    </PanelLateral>
  );
}

function Dato({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div>
      <dt className="text-[10px] font-semibold uppercase text-armazon-400">{rotulo}</dt>
      <dd className="mt-0.5 break-words uppercase text-armazon-800">{valor}</dd>
    </div>
  );
}
